import { geoCircle, geoGraticule, geoCentroid } from 'd3-geo';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const flattenCoordinates = (coords, out = []) => {
  if (!Array.isArray(coords)) {
    return out;
  }
  if (typeof coords[0] === 'number') {
    out.push(coords);
    return out;
  }
  coords.forEach((inner) => flattenCoordinates(inner, out));
  return out;
};

export const computeProjectionBounds = (features, projector) => {
  const points = [];
  features.forEach((feature) => {
    flattenCoordinates(feature.geometry.coordinates).forEach(([lng, lat]) => {
      const projected = projector(lng, lat);
      if (projected && Number.isFinite(projected[0]) && Number.isFinite(projected[1])) {
        points.push(projected);
      }
    });
  });

  if (!points.length) {
    return {
      minX: -180,
      maxX: 180,
      minY: -90,
      maxY: 90,
    };
  }

  const xs = points.map((point) => point[0]);
  const ys = points.map((point) => point[1]);

  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
};

export const projectToPseudoLonLat = (lng, lat, projector, bounds) => {
  const projected = projector(lng, lat);
  if (!projected) {
    return null;
  }

  const width = Math.max(1e-9, bounds.maxX - bounds.minX);
  const height = Math.max(1e-9, bounds.maxY - bounds.minY);

  const normX = ((projected[0] - bounds.minX) / width) * 360 - 180;
  const normY = 90 - ((projected[1] - bounds.minY) / height) * 180;

  return {
    lng: clamp(normX, -180, 180),
    lat: clamp(normY, -90, 90),
  };
};

const remapCoordArray = (coords, projector, bounds) => {
  if (!Array.isArray(coords)) {
    return null;
  }

  if (typeof coords[0] === 'number') {
    const mapped = projectToPseudoLonLat(coords[0], coords[1], projector, bounds);
    return mapped ? [mapped.lng, mapped.lat] : null;
  }

  const mappedChildren = coords
    .map((child) => remapCoordArray(child, projector, bounds))
    .filter(Boolean);

  return mappedChildren.length ? mappedChildren : null;
};

export const warpFeatureGeometry = (feature, projector, bounds) => {
  const remapped = remapCoordArray(feature.geometry.coordinates, projector, bounds);
  if (!remapped) {
    return null;
  }

  return {
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: remapped,
    },
  };
};

const distance2d = (a, b) => {
  if (!a || !b) {
    return 0;
  }
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return Math.sqrt(dx * dx + dy * dy);
};

export const distortionScore = (lng, lat, projector) => {
  const d = 0.6;
  const p = projector(lng, lat);
  const px = projector(lng + d, lat);
  const py = projector(lng, clamp(lat + d, -89.9, 89.9));

  if (!p || !px || !py) {
    return 0;
  }

  const sx = Math.max(1e-9, distance2d(p, px));
  const sy = Math.max(1e-9, distance2d(p, py));
  const anisotropy = Math.abs(sx - sy) / (sx + sy);
  const areaBias = Math.abs(Math.log(sx * sy));

  return clamp(anisotropy * 0.65 + areaBias * 0.12, 0, 1);
};

export const heatColor = (score) => {
  const s = clamp(score, 0, 1);
  const r = Math.round(20 + 235 * s);
  const g = Math.round(120 - 80 * s);
  const b = Math.round(255 - 240 * s);
  return `rgba(${r}, ${g}, ${b}, 0.72)`;
};

export const buildGraticulePaths = (projector, bounds) => {
  const graticule = geoGraticule().step([15, 15])();

  return graticule.coordinates
    .map((line) => {
      const coords = line
        .map(([lng, lat]) => projectToPseudoLonLat(lng, lat, projector, bounds))
        .filter(Boolean)
        .map((pt) => ({ lat: pt.lat, lng: pt.lng }));

      return coords.length > 1 ? { kind: 'graticule', coords } : null;
    })
    .filter(Boolean);
};

export const buildIndicatrixPaths = (projector, bounds) => {
  const circles = [];

  for (let lat = -75; lat <= 75; lat += 30) {
    for (let lng = -180; lng <= 180; lng += 30) {
      const circle = geoCircle().center([lng, lat]).radius(6).precision(4)();
      const ring = circle.coordinates[0] || [];
      const score = distortionScore(lng, lat, projector);

      const coords = ring
        .map(([ringLng, ringLat]) =>
          projectToPseudoLonLat(ringLng, ringLat, projector, bounds)
        )
        .filter(Boolean)
        .map((pt) => ({ lat: pt.lat, lng: pt.lng }));

      if (coords.length > 2) {
        circles.push({
          kind: 'indicatrix',
          score,
          coords,
        });
      }
    }
  }

  return circles;
};

export const buildDistortionHeatPoints = (projector, bounds) => {
  const points = [];

  for (let lat = -78; lat <= 78; lat += 8) {
    for (let lng = -176; lng <= 176; lng += 8) {
      const point = projectToPseudoLonLat(lng, lat, projector, bounds);
      if (!point) {
        continue;
      }
      const score = distortionScore(lng, lat, projector);
      points.push({
        lat: point.lat,
        lng: point.lng,
        score,
      });
    }
  }

  return points;
};

export const addDistortionToCountries = (countries, projector) =>
  countries.map((feature) => {
    const [lng, lat] = geoCentroid(feature);
    const score = distortionScore(lng, lat, projector);
    return {
      ...feature,
      properties: {
        ...(feature.properties || {}),
        distortionScore: score,
      },
    };
  });
